import React from 'react';

/**
 * Pagination
 * Renders page buttons with ellipsis logic, prev/next arrows.
 *
 * @param {{ currentPage: number, totalPages: number, onPageChange: function }} props
 */
export default function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = buildPageRange(currentPage, totalPages);

  return (
    <nav className="cs-pagination" aria-label="การแบ่งหน้า">
      {/* Prev */}
      <button
        className="cs-page-btn cs-page-btn--nav"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="หน้าก่อนหน้า"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Page buttons */}
      {pages.map((page, index) =>
        page === '...' ? (
          <span key={`ellipsis-${index}`} className="cs-page-ellipsis">
            &hellip;
          </span>
        ) : (
          <button
            key={page}
            className={`cs-page-btn ${currentPage === page ? 'cs-page-btn--active' : ''}`}
            onClick={() => onPageChange(page)}
            aria-label={`หน้า ${page}`}
            aria-current={currentPage === page ? 'page' : undefined}
          >
            {page}
          </button>
        )
      )}

      {/* Next */}
      <button
        className="cs-page-btn cs-page-btn--nav"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="หน้าถัดไป"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </nav>
  );
}

/**
 * Build the array of page numbers (and '...' ellipsis markers) to display.
 * Always shows first, last, current, and ±2 neighbours.
 *
 * @param {number} current
 * @param {number} total
 * @returns {Array<number|string>}
 */
function buildPageRange(current, total) {
  if (total <= 7) {
    // Show all pages if 7 or fewer
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set([1, total, current]);

  // Add neighbours
  for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) {
    pages.add(i);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];

  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
      result.push('...');
    }
  }

  return result;
}
